import { useEffect, useRef, useState } from "react";
import { Play, Pause, Download, Loader2, PenLine } from "lucide-react";
import { Equalizer } from "@/components/Equalizer";
import { siteDownloadUrl } from "@/lib/api";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const MasterTrackCard = ({ siteKey, sessionId, track, label, latestVersion = 1, ready, revisionInFlight, failed = false, recovering = false, onRevise, canRevise = true }) => {
  const audioRef = useRef(null);
  const [v, setV] = useState(latestVersion);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Follow the newest cut when a revision lands
  useEffect(() => { setV(latestVersion); }, [latestVersion]);

  if (!ready) {
    return (
      <div data-testid={`track-card-${track}`} className="glass-panel rounded-2xl px-4 sm:px-5 py-5 border-amber-400/30">
        <div className="flex items-center gap-3">
          <Loader2 className={`h-5 w-5 shrink-0 ${failed ? "text-coral" : "animate-spin text-amber-400"}`} />
          <div>
            <p className="font-display font-bold text-sm sm:text-base">{label}</p>
            <p data-testid={`track-${track}-pending`} className={`text-xs mt-0.5 ${failed ? "text-coral" : "text-amber-300/80"}`}>
              {failed
                ? "This take needs a studio hand — Pepper has been alerted."
                : recovering
                  ? "The band is still polishing the mix — taking a little longer than expected."
                  : "In the studio — this page updates itself when the clean cut lands."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const revisionBanner = revisionInFlight && (
    <div
      data-testid={`track-${track}-revision-inflight`}
      className="mb-3 rounded-xl border border-amber-400/50 bg-amber-400/10 px-4 py-3 flex items-center gap-3 animate-pulse"
    >
      <Loader2 className="h-5 w-5 animate-spin text-amber-300 shrink-0" />
      <div>
        <p className="font-display font-extrabold text-sm sm:text-base text-amber-300 uppercase tracking-wide">
          Revision in the studio
        </p>
        <p className="text-xs text-amber-200/80 mt-0.5">
          We're re-cutting this track now — the new version appears here automatically. No need to refresh.
        </p>
      </div>
    </div>
  );

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      document.querySelectorAll("audio").forEach((a) => a !== el && a.pause());
      el.play();
    }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <div data-testid={`track-card-${track}`} className="glass-panel rounded-2xl px-4 sm:px-5 py-4">
      <audio
        key={v}
        ref={audioRef}
        src={siteDownloadUrl(siteKey, track, sessionId, { v, inline: true })}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => setTime(e.target.currentTime)}
      />
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <p className="font-display font-bold text-sm sm:text-base leading-tight">{label}</p>
        {latestVersion > 1 && (
          <span className="flex items-center gap-1">
            {Array.from({ length: latestVersion }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                data-testid={`track-${track}-version-pill-${n}`}
                onClick={() => setV(n)}
                className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-2.5 py-0.5 cursor-pointer transition-colors ${
                  v === n ? "bg-gold text-black" : "border border-white/20 text-slate-400 hover:border-gold hover:text-gold"
                }`}
              >
                Version {n}
              </button>
            ))}
          </span>
        )}
      </div>
      {revisionBanner}
      <div className="flex items-center gap-3 sm:gap-4">
        <button
          data-testid={`track-${track}-play-btn`}
          onClick={toggle}
          className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-gold to-[#FB8500] flex items-center justify-center shadow-[0_0_20px_rgba(255,183,3,0.35)] hover:scale-105 active:scale-95 transition-transform cursor-pointer"
          aria-label={playing ? `Pause ${label}` : `Play ${label}`}
        >
          {playing ? <Pause className="h-5 w-5 text-black" /> : <Play className="h-5 w-5 text-black ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <div
              data-testid={`track-${track}-progress-bar`}
              onClick={seek}
              className="flex-1 h-1.5 rounded-full bg-white/10 cursor-pointer"
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-gold to-coral transition-[width] duration-100"
                style={{ width: `${duration ? (time / duration) * 100 : 0}%` }}
              />
            </div>
            <span className="font-mono text-[10px] text-slate-500 shrink-0">{fmt(time)} / {fmt(duration)}</span>
            <Equalizer playing={playing} color="#00F0FF" className="h-4 hidden sm:flex" />
          </div>
        </div>
        <a
          data-testid={`track-${track}-download-btn`}
          href={siteDownloadUrl(siteKey, track, sessionId, { v })}
          download
          aria-label={`Download ${label}`}
          className="shrink-0 h-11 w-11 rounded-xl border border-emerald-400/40 bg-emerald-400/10 flex items-center justify-center text-emerald-400 hover:bg-emerald-400/20 hover:border-emerald-400 transition-colors"
        >
          <Download className="h-5 w-5" />
        </a>
        {canRevise && (
          <button
            data-testid={`track-${track}-revise-btn`}
            onClick={() => onRevise(track, label, v)}
            disabled={!!revisionInFlight}
            className="shrink-0 h-11 px-3.5 rounded-xl border border-white/15 text-slate-300 text-xs font-bold flex items-center gap-1.5 hover:border-gold hover:text-gold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <PenLine className="h-4 w-4" />
            <span className="hidden sm:inline">Revise</span>
          </button>
        )}
      </div>
    </div>
  );
};
